import { Vehicle, type Vendor } from '@cruz-com/lookup';
import _ from 'lodash';
import { JsonLd } from 'react-schemaorg';
import type Schema from 'schema-dts';
import slugify from 'slugify';
import { pages, webBaseUrl } from '@/settings';
type Props = {
  name?: string;
  imageUrl?: string;
  vehicleList: Vehicle.Summary[] | undefined;
};
const lkpCondition = {
  new: 'https://schema.org/NewCondition',
  used: 'https://schema.org/UsedCondition',
  certified: 'https://schema.org/UsedCondition',
  repairable: 'https://schema.org/DamagedCondition'
};
const lkpDriveType = {
  AWD: 'AllWheelDriveConfiguration',
  FWD: 'FrontWheelDriveConfiguration',
  '4X4': 'FourWheelDriveConfiguration',
  '4X2': 'FourWheelDriveConfiguration',
  RWD: 'RearWheelDriveConfiguration'
};
export const VehicleListSchema = ({
  name,
  imageUrl,
  vehicleList
}: Props) => {
  try {
    if (_.isEmpty(vehicleList)) {
      return null;
    }
    const inventoryItems: Schema.ListItem[] = _.map(vehicleList, (v, idx) => {
      const {
        _id,
        condition,
        sale_status_id,
        make,
        model,
        year,
        trim,
        vin,
        mileage,
        reference,
        location,
        photo_preview_url,
        engine,
        user_display
      } = v || {};
      const {
        city,
        state_abbr,
        zip_code
      } = location || {};
      const title = _.compact([year, make, model, trim]).join(' ');
      const slugLocation = slugify([city, state_abbr, zip_code].join(' '), {
        lower: true
      });
      const url = webBaseUrl + pages.vehicleDetail.href.replace('[location]', slugLocation).replace('[title]', slugify(title, {
        lower: true
      })) + '/' + _id;
      const hasOffer = sale_status_id === Vehicle.SaleStatusEnum.ForSale || sale_status_id === Vehicle.SaleStatusEnum.OpenToOffers || sale_status_id !== Vehicle.SaleStatusEnum.Sold;
      function getSeller(): Schema.Corporation | Schema.Person | null {
        const loc = v?.location as Vendor.Location;
        if (loc?.name) {
          const dealerName = loc?.place?.display_name || loc?.name;
          const x: Schema.Corporation = {
            '@type': 'Corporation',
            legalName: dealerName,
            name: dealerName,
            address: {
              '@type': 'PostalAddress',
              addressLocality: loc?.city,
              addressRegion: loc?.state_abbr,
              postalCode: loc?.zip_code,
              streetAddress: loc?.address1,
              addressCountry: 'US'
            },
            telephone: loc.phone
          };
          if (!_.isEmpty(loc?.place)) {
            if ((loc?.place?.rating || 0) > 0) {
              x.aggregateRating = {
                '@type': 'AggregateRating',
                ratingValue: loc.place.rating
              };
            }
            x.description = loc?.place?.overview;
          }
          return x;
        }
        if (!_.isEmpty(user_display)) {
          return {
            '@type': 'Person',
            name: `${user_display?.first_name} ${_.first(user_display?.last_name)}`,
            address: {
              '@type': 'PostalAddress',
              addressLocality: loc?.city || '',
              addressRegion: loc?.state_abbr || '',
              addressCountry: 'US',
              postalCode: loc?.zip_code || ''
            }
          };
        }
        return null;
      }
      const d: Schema.Vehicle & {
        offers?: Schema.Offer;
      } = {
        '@type': 'Vehicle',
        name: title,
        vehicleModelDate: year?.toString(),
        brand: {
          '@type': 'Corporation',
          name: make
        },
        model: model,
        vehicleIdentificationNumber: vin || '',
        image: photo_preview_url || `${webBaseUrl}/img/noPhoto.png`,
        url,
        mileageFromOdometer: {
          '@type': 'QuantitativeValue',
          value: mileage || 1,
          unitText: 'mile',
          unitCode: 'SMI'
        },
        sku: reference?.stock_number || _id as string,
        itemCondition: (_.get(lkpCondition, condition || '') || 'UsedCondition') as Schema.OfferItemCondition,
        fuelType: engine?.fuel_type_name || '',
        vehicleTransmission: v?.transmission?.name
      };
      if ((v?.price || 0) > 0) {
        d.offers = {
          '@type': 'Offer',
          price: v.price_sort_lh,
          priceCurrency: 'USD',
          url,
          availability: hasOffer ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          itemCondition: _.get(lkpCondition, condition || '') || 'https://schema.org/UsedCondition',
          seller: getSeller() || undefined
        };
      }
      if (v?.drive_type) {
        const x_0 = _.get(lkpDriveType, v.drive_type);
        if (x_0) {
          d.driveWheelConfiguration = x_0;
        }
      }
      if (!_.isEmpty(v?.engine)) {
        d.vehicleEngine = {
          '@type': 'EngineSpecification',
          engineDisplacement: {
            '@type': 'QuantitativeValue',
            value: engine?.displacement || 0,
            unitCode: 'LTR'
          },
          fuelType: engine?.fuel_type || '',
          enginePower: {
            '@type': 'QuantitativeValue',
            value: engine?.max_hp || 0,
            unitCode: 'N12'
          },
          torque: {
            '@type': 'QuantitativeValue',
            value: engine?.max_torque || 0,
            unitCode: 'F17'
          },
          engineType: engine?.aspiration_name || '',
          description: engine?.name || ''
        };
      }
      if (!_.isEmpty(v.mpg_list)) {
        d.fuelEfficiency = [{
          '@type': 'QuantitativeValue',
          value: _.max(_.map(v.mpg_list, 'city')),
          unitText: 'mpg (city)'
        }, {
          '@type': 'QuantitativeValue',
          value: _.max(_.map(v.mpg_list, 'highway')),
          unitText: 'mpg (highway)'
        }];
      }
      if (v?.specification?.max_seating) {
        d.seatingCapacity = v.specification.max_seating;
      }
      if (v?.review_score?.overall) {
        d.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: v.review_score.overall,
          ratingCount: 1
        };
      }
      const item: Schema.ListItem = {
        '@type': 'ListItem',
        position: idx + 1,
        url,
        item: d
      };
      return item;
    });
    const data: Schema.ItemList & {
      '@context': 'https://schema.org';
    } = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name,
      itemListElement: inventoryItems,
      image: imageUrl ? webBaseUrl + imageUrl : `${webBaseUrl}/img/noPhoto.png`
    };
    return <JsonLd<Schema.ItemList> item={data} />;
  } catch {
    return null;
  }
};